import React from 'react';
import { yup } from 'shared/core/yup';
import { FieldErrors } from 'react-hook-form';

import Form from 'src/components/forms/form';
import { StepHeader, StepPanel, StepFooter } from '../shared-components';
import {
  BooleanRadio,
  CustomRadio,
  FreeTextInput,
  ConditionalFieldWrapper,
  FormSelectInput,
  SubmitButton,
  GoodCannaFooter,
} from './med-intake-ui';

import { goodCannaSchema, MEDICAL_CONDITIONS } from './good-canna-schema';

export type GoodCannaFieldsType = yup.InferType<typeof goodCannaSchema> & {
  medicalCondition: typeof MEDICAL_CONDITIONS[number];
};

type GoodCannaStepProps = {
  onNext: (data: { goodCanna: GoodCannaFieldsType }) => void;
  onBack: () => void;
  defaultValues?: GoodCannaFieldsType;
};

export const GoodCannaStep = ({ onNext, onBack, defaultValues }: GoodCannaStepProps): JSX.Element => {
  const handleSubmit = (data: GoodCannaFieldsType): void => {
    onNext({ goodCanna: data });
  };

  const handleError = (errors: FieldErrors<GoodCannaFieldsType>): void => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);

      if (errorElement) {
        requestAnimationFrame(() => {
          const elementPosition = errorElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 300;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        });
      }
    }
  };

  return (
    <>
      <StepHeader
        onBack={onBack}
        showBackButton
        stepHeading='Patient Intake Form'
        stepDescription='Accurate health information is crucial for the doctor to make informed & effective medical decisions regarding your care.'
      />
      <Form
        onSubmit={handleSubmit}
        onError={handleError}
        validationSchema={goodCannaSchema}
        defaultValues={defaultValues}
        shouldUnregister
      >
        <StepPanel>
          <FormSelectInput
            name='medicalCondition'
            id='medicalCondition'
            label='Qualifying medical condition (choose one)'
            defaultValue={defaultValues?.medicalCondition ?? ''}
          >
            {MEDICAL_CONDITIONS.map((condition) => (
              <option key={condition} value={condition}>
                {condition}
              </option>
            ))}
          </FormSelectInput>

          <BooleanRadio question='Are you physically disabled?' name='physicallyDisabled' />

          <CustomRadio
            question='What was your gender at birth?'
            name='gender'
            options={[
              { label: 'Male', value: 'Male' },
              { label: 'Female', value: 'Female' },
            ]}
            defaultValue={defaultValues?.gender}
          />
          <ConditionalFieldWrapper watchFieldName='gender' showWhen='Female'>
            <BooleanRadio question='Are you currently pregnant?' name='currentlyPregnant' />
            <BooleanRadio question='Are you currently breastfeeding?' name='currentlyBreastfeeding' />
          </ConditionalFieldWrapper>

          <BooleanRadio
            question='Do you currently have, or have you ever had, schizophrenia, psychosis, or any suicidal thoughts or ideation?'
            name='mentalConditions'
            defaultValue={defaultValues?.mentalConditions}
          />
          <ConditionalFieldWrapper watchFieldName='mentalConditions'>
            <BooleanRadio
              question='Do you have a history of suicidal thoughts and ideations?'
              name='historyOfSuicide'
            />
            <FreeTextInput
              question='Please list any mental health conditions you have been diagnosed with'
              name='listMentalHealthConditions'
              placeholder='Enter your mental health conditions'
            />
            <FreeTextInput
              question='Please list any current medications you are taking'
              name='mentalMedications'
              placeholder='Enter your current medications'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Have you been diagnosed with bipolar disorder?' name='bipolar' />
          <ConditionalFieldWrapper watchFieldName='bipolar'>
            <BooleanRadio question='Is your condition stable?' name='bipolarStable' />
            <FreeTextInput
              question='Are you taking any medications for Bipolar disorder: if so, which ones?'
              name='bipolarMedications'
              placeholder='Enter your medications'
            />
            <FreeTextInput
              question='Who is currently managing your bipolar disorder?'
              name='bipolarManager'
              placeholder='Enter your care provider'
            />
            <BooleanRadio
              question='Do you understand and acknowledge the potential risks of using medical marijuana while having Bipolar Disorder, including the possibility of worsening mood episodes?'
              name='bipolarRisk'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Are you currently taking any opioids or pain medications?' name='opioidsOrLithium' />
          <ConditionalFieldWrapper watchFieldName='opioidsOrLithium'>
            <FreeTextInput
              question='Please explain the reason for taking pain medication'
              name='opioidsOrLithiumReason'
              placeholder='Enter your reason for taking pain medication'
            />
            <FreeTextInput
              question='Please list which type of doctor is prescribing'
              name='opioidsOrLithiumDoctor'
              placeholder='Enter your type of doctor'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio
            question='Have you been hospitalized or had any surgeries in the last 6 months?'
            name='hospitalizedOrSurguries'
          />
          <ConditionalFieldWrapper watchFieldName='hospitalizedOrSurguries'>
            <FreeTextInput
              question='Please explain the hospitalization or surgery'
              name='recentHospitalizationReason'
              placeholder='Enter your reason for being hospitalized'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Are you currently taking any prescription medications?' name='takingMedications' />
          <ConditionalFieldWrapper watchFieldName='takingMedications'>
            <FreeTextInput
              question='Please list which prescription medications you are taking'
              name='listOfMedications'
              placeholder='Enter your prescription medications'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Do you have any allergies to medications' name='medAllergies' />
          <ConditionalFieldWrapper watchFieldName='medAllergies'>
            <FreeTextInput
              question='Please list which medications you are allergic to'
              name='listOfAllergies'
              placeholder='Enter your allergies'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Do you currently use tobacco products?' name='tobaccoUse' />
          <ConditionalFieldWrapper watchFieldName='tobaccoUse'>
            <FreeTextInput
              question='Please list which tobacco products you use and how often'
              name='howOftenTobacco'
              placeholder='Enter your tobacco products'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Do you currently drink alcohol?' name='alcoholUse' />
          <ConditionalFieldWrapper watchFieldName='alcoholUse'>
            <FreeTextInput
              question='Which alcohol products and how often?'
              name='howOftenAlcohol'
              placeholder='Enter your alcohol products used'
            />
          </ConditionalFieldWrapper>

          <BooleanRadio question='Are you currently using any cannabis products?' name='currentMedicalCannabisUse' />
        </StepPanel>
        <StepFooter>
          <SubmitButton>Continue</SubmitButton>
          <GoodCannaFooter />
        </StepFooter>
      </Form>
    </>
  );
};
